import {
    flattenPerspective,
    getOutputDimensions,
} from '../../image/flatten/flattenDocumentEngine';

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

/**
 * Build a perspective-flattened preview blob URL from a page image + normalized corners.
 * @param {string} imageSrc
 * @param {{ x: number, y: number }[]} corners TL,TR,BR,BL in 0–1
 * @returns {Promise<string|null>} object URL (caller must revoke)
 */
export async function buildFlattenPreviewUrl(imageSrc, corners) {
    if (!imageSrc || !Array.isArray(corners) || corners.length < 4) return null;

    const img = await loadImage(imageSrc);
    const nw = img.naturalWidth || img.width;
    const nh = img.naturalHeight || img.height;
    if (!nw || !nh) return null;

    const naturalCorners = corners.map((c) => ({
        x: c.x * nw,
        y: c.y * nh,
    }));
    const { width: outW, height: outH } = getOutputDimensions(naturalCorners);
    if (outW < 2 || outH < 2) return null;

    const blob = await flattenPerspective(
        imageSrc,
        { width: nw, height: nh },
        0,
        naturalCorners,
        Math.max(40, Math.round(outW)),
        Math.max(40, Math.round(outH)),
        'image/jpeg',
    );
    if (!blob) return null;
    return URL.createObjectURL(blob);
}

/** Stable key for a flatten corner set (for cache invalidation). */
export function flattenCornersKey(corners) {
    if (!corners?.length) return '';
    return corners.map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)}`).join('|');
}
